<template>
  <Tooltip placement="top-start" :disabled="isDisabled">
    <template #trigger>
      <div
        :class="pillClass"
        :style="{ backgroundColor: halfColor }"
        @click="emitAndDisableTooltip('click-tag')"
      >
        <span
          v-if="showColorDot"
          :style="{ backgroundColor: tag.color }"
          class="color-dot"
        ></span>
        <span :class="labelClass" :style="tagColorLabel">{{ tag.label }}</span>
        <div v-if="hasActions" class="sera-ml-2 actions-menu" ignore-card-click>
          <span
            class="fas fa-trash-alt text-gray"
            :style="tagColorLabel"
            @click="emitAndDisableTooltip('remove-tag')"
          ></span>
        </div>
      </div>
    </template>
    <template #tooltip>
      <div class="tag-tooltip">
        {{ description }}
      </div>
    </template>
  </Tooltip>
</template>

<script setup lang="ts">
import tinycolor from "tinycolor2";
import { computed, onBeforeMount, ref } from "vue";

import Tooltip from "@/components/Tooltip.vue";
import { Tag } from "@/typings/tag";

const props = defineProps({
  tag: {
    type: Tag,
    required: true
  },
  editable: {
    type: Boolean,
    default: false
  },
  removeable: {
    type: Boolean,
    default: false
  },
  smallPill: {
    type: Boolean,
    default: false
  },
  colorLabel: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["remove-tag", "click-tag"]);

// Function to decide whether to display the actions menu
const hasActions = computed(() => props.editable || props.removeable);

const descTooltipOptions = computed(() => {
  return {
    interactive: false,
    delay: [0, 0]
  };
});

const isDisabled = ref(false);

const emitAndDisableTooltip = (emitType) => {
  isDisabled.value = true;
  emit(emitType);
};

// All colors can be found on the BE app/models/tag.rb
const lightenSensitiveColors = ["Pink", "Hot Pink", "Indigo", "Violet"];

const pillClass = props.smallPill ? "tag-pill-small tag-pill" : "tag-pill";

const labelClass = "tag-label";

const description = props.tag.description || "No description available";

const halfColor = ref("");

onBeforeMount(() => {
  if (props.tag.color) resolveBackgroundColor();
});

// 4 colors have issues with background due to lightening sensitivity, so tweaking was necessary
const resolveBackgroundColor = () => {
  if (props.tag.color_label === "Pink")
    halfColor.value = tinycolor(props.tag.color || "#fff").lighten(13);
  else if (lightenSensitiveColors.includes(props.tag.color_label))
    halfColor.value = tinycolor(props.tag.color || "#fff").lighten(23);
  else halfColor.value = tinycolor(props.tag.color || "#fff").lighten(33);
};

const showColorDot = computed(() => {
  return !props.colorLabel;
});

const tagColorLabel = computed(() => {
  if (props.colorLabel) {
    return {
      color: props.tag.color
    };
  }

  return {};
});
</script>

<style scoped>
.more-actions {
  width: 20px;
  cursor: pointer;
  height: 26px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 3px;
}
.more-actions:hover {
  background: var(--sera-color-core-blue-light-blue-100);
}
</style>
